import React from 'react'
import { useHistory } from 'react-router-dom'
import styles from './index.module.scss'

const illegalContentMap = {
  title: '遇到不适当/恶意内容怎么办?',
  description:
    '如果内容涉及色情、犯罪、违禁品、赌博、暴力等不适当或恶意内容，可继续对其进行投诉。',
}

/**
 * 投诉类型
 */
export interface ComplaintType {
  id: string
  title?: string
  description?: string
  levelpage: '2' | '3'
  // 预览信息
  previewInfo?: {
    title?: string
    description?: string
    list?: ComplaintType[] // 2级页面下的子类型
  }
  // 选择列表信息
  selectListInfo?: {
    title?: string
    list?: Pick<
      ComplaintType,
      'id' | 'title' | 'description' | 'levelpage' | 'previewInfo'
    >[]
  }
}

/**
 * 投诉类型列表
 * levelpage表示点击后进入的页面等级，2级页面需要继续选择投诉类型（点击后进入3级页面）、3级页面展示描述信息
 */
export const COMPLAINT_TYPES: ComplaintType[] = [
  {
    id: 'fraud',
    title: '网页包含欺诈信息',
    description: '如假红包等',
    levelpage: '3', // 3级页面
    previewInfo: {
      title: '遇到欺诈等恶意内容怎么办?',
      description:
        '如遇诈骗并发生资金损失时，建议尽快拨打110 或联系当地公安机关报案。你也可以通过下方入口投诉，我们会在核实信息后进行处理。',
    },
  },
  {
    id: 'illegal',
    title: '网页包含违法信息',
    levelpage: '2', // 2级页面
    selectListInfo: {
      title: '选择违法类型',
      list: [
        {
          id: 'illegal_content1',
          title: '色情',
          levelpage: '3',
          previewInfo: illegalContentMap,
        },
        {
          id: 'illegal_content2',
          title: '暴恐血腥',
          levelpage: '3',
          previewInfo: illegalContentMap,
        },
        {
          id: 'illegal_content3',
          title: '政治敏感',
          levelpage: '3',
          previewInfo: illegalContentMap,
        },
        {
          id: 'illegal_content4',
          title: '赌博',
          levelpage: '3',
          previewInfo: illegalContentMap,
        },
      ],
    },
  },
  {
    id: 'rumor',
    title: '网页可能包含谣言信息',
    levelpage: '3',
    previewInfo: illegalContentMap,
  },
  {
    id: 'induce_share',
    title: '网页包含诱导分享/关注性质的内容',
    levelpage: '2',
    selectListInfo: {
      title: '选择违法类型',
      list: [
        {
          id: 'induce_share_content1',
          title: '好友助力加速',
          levelpage: '3',
          previewInfo: illegalContentMap,
        },
        {
          id: 'induce_share_content2',
          title: '砍价',
          levelpage: '3',
          previewInfo: illegalContentMap,
        },
        {
          id: 'induce_share_content3',
          title: '任务收集',
          levelpage: '3',
          previewInfo: illegalContentMap,
        },
        {
          id: 'induce_share_content4',
          title: '诱导下载',
          levelpage: '3',
          previewInfo: illegalContentMap,
        },
      ],
    },
  },
  {
    id: 'false_group',
    title: '网页包含虚假/抽奖性质/欺诈的拼团信息',
    levelpage: '3',
    previewInfo: illegalContentMap,
  },
  {
    id: 'virtual_payment',
    title: '网页包含虚拟支付信息',
    levelpage: '3',
    previewInfo: illegalContentMap,
  },
  {
    id: 'harassment',
    title: '网页包含不适当的内容对我造成骚扰',
    levelpage: '2',
    selectListInfo: {
      title: '选择违法类型',
      list: [
        {
          id: 'harassment_content1',
          title: '内容低俗/图文不符',
          levelpage: '3',
          previewInfo: illegalContentMap,
        },
        {
          id: 'harassment_content2',
          title: '骚扰/广告/垃圾信息',
          levelpage: '3',
          previewInfo: illegalContentMap,
        },
      ],
    },
  },
  {
    id: 'paid_vote',
    title: '网页包含有偿投票信息',
    levelpage: '3',
    previewInfo: illegalContentMap,
  },
  {
    id: 'privacy_leak',
    title: '网页包含隐私泄漏风险',
    levelpage: '2',
    selectListInfo: {
      title: '选择违法类型',
      list: [
        {
          id: 'privacy_leak1',
          title: '收集个人隐私信息(账号密码/验证码等)',
          levelpage: '3',
          previewInfo: illegalContentMap,
        },
        {
          id: 'privacy_leak2',
          title: '违规使用用户头像',
          levelpage: '3',
          previewInfo: illegalContentMap,
        },
      ],
    },
  },
]

/**
 * 选择投诉类型
 */
export default function SelectComplaintType({
  title,
  lists = COMPLAINT_TYPES,
}: {
  title?: string
  lists?: ComplaintType[]
}) {
  const history = useHistory()

  /**
   * 处理类型选择
   */
  const onTypeSelect = (type: ComplaintType) => {
    history.push(`/complaint?type=${type.id}&level=${type.levelpage}`)
  }

  const handleDescClick = () => {
    window.open(
      'https://szsupport.weixin.qq.com/cgi-bin/mmsupportacctnodeweb-bin/pages/V22NcC3iJRqWtLc2?lang=zh_CN&wechat_real_lang=zh_CN&hashuin=401084927&pass_ticket=1xVYUZ6TCXkpawcqmRhgSWhUKjR7pqws4wKQ7fnWLUZRLDQULMn6Lza0wzHLdlW7fbTvyNXf2uE01vkpi0OTXQ%3D%3D&wx_header=2',
      '_blank',
    )
  }

  return (
    <div className={styles.container}>
      <div className={styles.title}>
        <div>{title || '选择投诉原因'}</div>
        {!title && (
          <div className={styles.desc} onClick={handleDescClick}>
            遇到网页流量被劫持怎么办？
          </div>
        )}
      </div>

      <div className={styles.type_list}>
        {lists.map(type => (
          <div
            key={type.id}
            className={styles.type_item}
            onClick={() => onTypeSelect(type)}
          >
            <div className={styles.type_item_content}>
              <div className={styles.type_item_title}>{type.title}</div>
              {type.description && (
                <div className={styles.type_item_description}>
                  {type.description}
                </div>
              )}
            </div>
            <div className={styles.type_item_arrow}></div>
          </div>
        ))}
      </div>
    </div>
  )
}
